import {
  Glass,
  Dot,
  ScreenLabel,
  DisplayText,
  BodyText,
  PillChip,
  SkillSigil,
  alpha,
  borderAlpha,
} from "../../components/mobile/primitives";

// ─── Screen 1: User logs a meal ─────────────────────────────────────────────

export function FlowCaptureFood() {
  return (
    <div style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Greeting */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingLeft: 4 }}>
        <ScreenLabel style={{ letterSpacing: "0.25em" }}>Thu, Mar 6</ScreenLabel>
        <DisplayText size={28} color="var(--color-kin-black)" lineHeight={1.1}>
          Good evening,
        </DisplayText>
        <DisplayText size={28} color="#3A4A3D" style={{ fontStyle: "italic" }}>
          Sverre
        </DisplayText>
      </div>

      {/* Saved confirmation — the meal was just logged */}
      <Glass style={{ padding: "12px 14px", borderRadius: 20 }}>
        {/* Saved header */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 18 18"
            style={{ flexShrink: 0, filter: "drop-shadow(0 1px 3px rgba(91,122,102,0.3))" }}
          >
            <circle cx="9" cy="9" r="9" fill="var(--color-kin-green-soft)" />
            <path
              d="M5 9 L8 12 L13 6"
              stroke="var(--color-kin-white)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
          <BodyText size={11} color="var(--color-kin-green-soft)" weight={800}>
            Meal logged
          </BodyText>
          <BodyText size={8} color="#b0a89f" weight={500} style={{ marginLeft: "auto" }}>
            Just now
          </BodyText>
        </div>
        {/* The saved content */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              background: alpha("var(--color-kin-gold)", "14"),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-kin-gold)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 4h12v16H6z" />
              <path d="M9 8h6" />
              <path d="M9 12h4" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <BodyText size={11} weight={700} color="var(--color-kin-black)">
              Dinner · 7:42 PM
            </BodyText>
            <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
              Grilled salmon, brown rice, steamed broccoli
            </BodyText>
          </div>
        </div>
      </Glass>

      {/* Today's trackers below */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
            paddingLeft: 4,
            paddingRight: 4,
          }}
        >
          <ScreenLabel style={{ letterSpacing: "0.2em" }}>Today's Trackers</ScreenLabel>
          <BodyText size={9} color="var(--color-kin-gold)" weight={700}>
            See logs →
          </BodyText>
        </div>
        <Glass style={{ padding: "4px 14px", borderRadius: 20 }}>
          {[
            {
              color: "var(--color-kin-gold)",
              label: "Diet",
              detail: "Meal log",
              status: "4 meals",
              statusColor: "var(--color-kin-green-soft)",
            },
            {
              color: "var(--color-kin-lavender)",
              label: "Mood",
              detail: "Daily check-in",
              status: "Log",
              action: true,
            },
            {
              color: "#ff6b6b",
              label: "Headache",
              detail: "Symptom log",
              status: "None today",
              statusColor: "var(--color-kin-green-soft)",
            },
          ].map((t, i) => (
            <div
              key={t.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 0",
                borderBottom: i < 2 ? "1px solid rgba(0,0,0,0.04)" : "none",
              }}
            >
              <Dot
                color={t.color}
                size={8}
                style={{ boxShadow: `0 0 6px ${alpha(t.color, "80")}` }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <BodyText size={11} weight={700} color="var(--color-kin-black)">
                  {t.label}
                </BodyText>
                <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
                  {t.detail}
                </BodyText>
              </div>
              {t.action ? (
                <div
                  style={{
                    background: alpha(t.color, "15"),
                    border: borderAlpha(t.color, "30"),
                    borderRadius: 20,
                    padding: "3px 10px",
                  }}
                >
                  <BodyText size={8.5} color={t.color} weight={800}>
                    {t.status}
                  </BodyText>
                </div>
              ) : (
                <BodyText size={9} color={t.statusColor} weight={800}>
                  {t.status}
                </BodyText>
              )}
            </div>
          ))}
        </Glass>
      </div>
    </div>
  );
}

// ─── Screen 2: Skill reacts — home screen with reaction card ────────────────

export function FlowHomeSkillReacts() {
  return (
    <div style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Greeting */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingLeft: 4 }}>
        <ScreenLabel style={{ letterSpacing: "0.25em" }}>Thu, Mar 6</ScreenLabel>
        <DisplayText size={28} color="var(--color-kin-black)" lineHeight={1.1}>
          Good evening,
        </DisplayText>
        <DisplayText size={28} color="#3A4A3D" style={{ fontStyle: "italic" }}>
          Sverre
        </DisplayText>
      </div>

      {/* The meal entry with the skill's reaction attached */}
      <div>
        <div style={{ paddingLeft: 4, marginBottom: 10 }}>
          <ScreenLabel style={{ letterSpacing: "0.2em" }}>From Your Data</ScreenLabel>
        </div>
        <Glass style={{ padding: "12px 14px", borderRadius: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 9,
                background: alpha("var(--color-kin-gold)", "14"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-kin-gold)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 4h12v16H6z" />
                <path d="M9 8h6" />
                <path d="M9 12h4" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BodyText size={11} weight={700} color="var(--color-kin-black)">
                Dinner · 7:42 PM
              </BodyText>
              <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
                Grilled salmon, brown rice, broccoli
              </BodyText>
            </div>
            <BodyText
              size={8}
              color="var(--color-kin-muted)"
              weight={500}
              style={{ flexShrink: 0 }}
            >
              2m
            </BodyText>
          </div>
          {/* Extracted facts — from enrichment pipeline, objective */}
          <div
            style={{
              marginLeft: 40,
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: 5,
            }}
          >
            {[
              { text: "Protein ~35g", source: "salmon" },
              { text: "Complex carbs", source: "brown rice" },
              { text: "Omega-3", source: "salmon" },
            ].map((f) => (
              <div key={f.text} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <Dot color="var(--color-kin-muted)" size={4} />
                <BodyText size={9} weight={600} color="var(--color-kin-black)">
                  {f.text}
                </BodyText>
                <BodyText size={8.5} color="var(--color-kin-muted)" weight={500}>
                  {f.source}
                </BodyText>
              </div>
            ))}
          </div>

          {/* Skill reaction — from Nutrition Audit hook, subjective */}
          <div
            style={{
              marginLeft: 40,
              marginTop: 10,
              padding: "9px 11px",
              borderRadius: 12,
              background: "rgba(91,122,102,0.06)",
              border: "1px solid rgba(91,122,102,0.12)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <SkillSigil name="Nutrition Audit" color="var(--color-kin-green-soft)" />
            </div>
            <BodyText
              size={10}
              color="var(--color-kin-black)"
              weight={600}
              style={{ lineHeight: 1.45 }}
            >
              Protein on target (30g+). 5th balanced dinner in a row.
            </BodyText>
          </div>
        </Glass>
      </div>

      {/* Previous entries — showing the streak */}
      <Glass style={{ padding: "12px 14px", borderRadius: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              background: alpha("var(--color-kin-gold)", "14"),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-kin-gold)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 4h12v16H6z" />
              <path d="M9 8h6" />
              <path d="M9 12h4" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <BodyText size={11} weight={700} color="var(--color-kin-black)">
              Lunch · 12:15 PM
            </BodyText>
            <BodyText size={9} color="var(--color-kin-muted)" weight={500}>
              Chicken salad, avocado, sourdough
            </BodyText>
          </div>
          <BodyText size={8} color="var(--color-kin-muted)" weight={500} style={{ flexShrink: 0 }}>
            7h
          </BodyText>
        </div>
      </Glass>
    </div>
  );
}

// ─── Screen 3: Skills tab — skill shows recent reactions ────────────────────

export function FlowSkillsWithReactions() {
  const skills = [
    {
      accent: "var(--color-kin-green-soft)",
      title: "Nutrition Audit",
      author: "Jonas Weber",
      authorInitials: "JW",
      badge: "Active · 3 reactions today",
      badgeColor: "#2fb56a",
      sub: "Last reacted 2 min ago",
      state: "active",
      // The most recent reaction, shown inline
      reactionTrigger: "Dinner · Grilled salmon, brown rice, broccoli",
      reaction: "Great protein source — salmon hits your 30g target.",
      reactionTime: "2m ago",
      primary: { label: "View", filled: true },
      secondary: { label: "Chat" },
    },
    {
      accent: "var(--color-kin-lavender)",
      title: "Sleep Deep Dive",
      author: "Dr. Sara Chen",
      authorInitials: "SC",
      badge: "+8 nights, +1 lab",
      badgeColor: "var(--color-kin-lavender)",
      sub: "8 new sleep nights since the last run",
      state: "new-data",
      reaction: null,
      primary: { label: "Re-run", filled: true },
      secondary: { label: "View last" },
    },
    {
      accent: "#ff8a4d",
      title: "Stress Load Check",
      author: "Dr. Sara Chen",
      authorInitials: "SC",
      badge: "Ready to run",
      badgeColor: "#7e8b82",
      sub: "HRV drops vs workload markers",
      state: "ready",
      reaction: null,
      primary: { label: "Run", filled: true },
      secondary: null,
    },
  ];

  return (
    <div style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column", gap: 14 }}>
      <DisplayText
        size={28}
        color="var(--color-kin-black)"
        lineHeight={1.1}
        style={{ paddingLeft: 4 }}
      >
        Skills
      </DisplayText>

      {/* Category chips */}
      <div
        className="phone-scroll"
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "nowrap",
          overflowX: "auto",
          paddingLeft: 4,
          paddingBottom: 4,
        }}
      >
        {[
          ["All", true],
          ["Nutrition", false],
          ["Sleep", false],
          ["Lab", false],
        ].map(([label, active]) => (
          <PillChip key={label} active={active} color="var(--color-kin-green-soft)">
            {label}
          </PillChip>
        ))}
      </div>

      {/* Skill cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {skills.map((skill) => (
          <Glass key={skill.title} style={{ padding: "14px 14px", borderRadius: 24 }}>
            {/* Skill header */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              {/* Concentric square pulse — skill provenance glyph */}
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3.5,
                  border: borderAlpha(skill.accent, "40", "1.5px"),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 2,
                }}
              >
                <div
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 2,
                    background: skill.accent,
                  }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <BodyText size={11} weight={800} color="var(--color-kin-black)">
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                    }}
                  >
                    {skill.title}
                  </span>
                </BodyText>
                <BodyText
                  size={9}
                  color="var(--color-kin-muted)"
                  weight={500}
                  style={{ marginTop: 2 }}
                >
                  {skill.sub}
                </BodyText>
              </div>
              <div
                style={{
                  padding: "3px 8px",
                  borderRadius: 10,
                  background: alpha(skill.badgeColor, "15"),
                  border: borderAlpha(skill.badgeColor, "30"),
                  fontSize: 8,
                  fontWeight: 800,
                  color: skill.badgeColor,
                  fontFamily: "'Manrope', system-ui",
                  flexShrink: 0,
                }}
              >
                {skill.badge}
              </div>
            </div>

            {/* Latest reaction — one line, tied to trigger */}
            {skill.reaction && (
              <div style={{ marginTop: 8, paddingLeft: 18 }}>
                <BodyText
                  size={9}
                  color="var(--color-kin-muted)"
                  weight={500}
                  style={{ marginBottom: 2 }}
                >
                  {skill.reactionTrigger} · {skill.reactionTime}
                </BodyText>
                <BodyText
                  size={10}
                  color="var(--color-kin-black)"
                  weight={600}
                  style={{ lineHeight: 1.4 }}
                >
                  {skill.reaction}
                </BodyText>
              </div>
            )}

            {/* Author + buttons */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 10,
              }}
            >
              <BodyText size={8.5} color="var(--color-kin-muted)" weight={600}>
                {skill.author}
              </BodyText>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {skill.secondary && (
                  <BodyText size={8.5} color={skill.accent} weight={700}>
                    {skill.secondary.label}
                  </BodyText>
                )}
                <div
                  style={{
                    padding: "6px 14px",
                    borderRadius: 20,
                    background: skill.primary.filled ? "#1b1d1a" : "transparent",
                    border: skill.primary.filled ? "none" : borderAlpha(skill.accent, "40"),
                    boxShadow: skill.primary.filled ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                  }}
                >
                  <BodyText
                    size={8.5}
                    color={skill.primary.filled ? "#ffffff" : skill.accent}
                    weight={800}
                  >
                    {skill.primary.label}
                  </BodyText>
                </div>
              </div>
            </div>
          </Glass>
        ))}
      </div>
    </div>
  );
}

// ─── Screen 4: Skill detail — reaction feed over time ───────────────────────

export function FlowSkillReactionFeed() {
  const reactions = [
    {
      time: "7:44 PM",
      date: "Today",
      trigger: "Grilled salmon, brown rice, broccoli",
      triggerLabel: "Dinner",
      message:
        "Great protein source — salmon hits your 30g target. This is your 5th balanced dinner in a row.",
      sentiment: "positive",
    },
    {
      time: "12:18 PM",
      date: "Today",
      trigger: "Chicken salad, avocado, sourdough",
      triggerLabel: "Lunch",
      message:
        "Solid meal. Avocado adds healthy fats. Your lunch protein has been consistent all week.",
      sentiment: "positive",
    },
    {
      time: "8:02 AM",
      date: "Today",
      trigger: "Oatmeal with mixed berries",
      triggerLabel: "Breakfast",
      message:
        "Good complex carbs. Consider adding a protein source (eggs, yogurt) to hit 25g at breakfast.",
      sentiment: "suggestion",
    },
    {
      time: "7:55 PM",
      date: "Yesterday",
      trigger: "Pasta with marinara sauce",
      triggerLabel: "Dinner",
      message:
        "Lower protein than your other dinners this week (est. 12g). Not a problem occasionally.",
      sentiment: "neutral",
    },
    {
      time: "1:10 PM",
      date: "Yesterday",
      trigger: "Turkey wrap, side salad",
      triggerLabel: "Lunch",
      message: "Good lean protein choice. You're averaging 28g protein at lunch this week.",
      sentiment: "positive",
    },
  ];

  const sentimentColor = {
    positive: "var(--color-kin-green-soft)",
    suggestion: "var(--color-kin-gold)",
    neutral: "var(--color-kin-muted)",
  };

  return (
    <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Hero band */}
      <div
        style={{
          margin: "0 -16px",
          padding: "14px 16px 14px",
          background: `linear-gradient(135deg, ${alpha("var(--color-kin-green-soft)", "14")}, ${alpha("var(--color-kin-green-soft)", "06")})`,
          borderBottom: borderAlpha("var(--color-kin-green-soft)", "20"),
        }}
      >
        <BodyText
          size={10}
          color="var(--color-kin-green-soft)"
          weight={800}
          style={{ marginBottom: 8 }}
        >
          ← Skills
        </BodyText>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <Dot size={10} color="var(--color-kin-green-soft)" />
          <BodyText
            size={8}
            color="var(--color-kin-green-soft)"
            weight={700}
            style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}
          >
            Nutrition
          </BodyText>
        </div>
        <DisplayText size={22} lineHeight={1.1} color="var(--color-kin-black)">
          Nutrition Audit
        </DisplayText>
        <BodyText size={9} color="var(--color-kin-muted)" weight={500} style={{ marginTop: 3 }}>
          Jonas Weber · Diet and nutrition
        </BodyText>
        <BodyText size={8} color="#b0a89f" weight={500} style={{ marginTop: 2 }}>
          3 reactions today · Full run 3 days ago
        </BodyText>
      </div>

      {/* Summary strip */}
      <div
        style={{
          margin: "14px 0 10px",
          display: "flex",
          gap: 8,
        }}
      >
        {[
          { v: "28g", l: "Avg Protein" },
          { v: "5", l: "Day Streak" },
          { v: "82%", l: "Balance" },
        ].map((kpi) => (
          <Glass
            key={kpi.l}
            style={{ flex: 1, padding: "10px 6px", textAlign: "center", borderRadius: 22 }}
          >
            <DisplayText size={18} color="var(--color-kin-black)">
              {kpi.v}
            </DisplayText>
            <ScreenLabel style={{ marginTop: 2 }}>{kpi.l}</ScreenLabel>
          </Glass>
        ))}
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8, marginTop: 2 }}>
        <div
          style={{
            flex: 1,
            padding: "10px 0",
            borderRadius: 20,
            background: "#1b1d1a",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            textAlign: "center",
          }}
        >
          <BodyText size={10} color="#ffffff" weight={800}>
            Re-run Analysis
          </BodyText>
        </div>
        <div
          style={{
            flex: 1,
            padding: "10px 0",
            borderRadius: 20,
            background: "rgba(254,253,250,0.58)",
            border: "1px solid rgba(91,122,102,0.25)",
            textAlign: "center",
          }}
        >
          <BodyText size={10} color="var(--color-kin-green-soft)" weight={800}>
            Chat
          </BodyText>
        </div>
      </div>

      {/* Reaction feed */}
      <div style={{ marginTop: 4 }}>
        <ScreenLabel style={{ marginBottom: 10, display: "block", paddingLeft: 4 }}>
          <span style={{ color: "var(--color-kin-green-soft)" }}>Live Reactions</span>
        </ScreenLabel>

        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            paddingLeft: 6,
          }}
        >
          {/* Vertical connecting line */}
          <div
            style={{
              position: "absolute",
              left: 10,
              top: 10,
              bottom: 10,
              width: 2,
              background: "linear-gradient(to bottom, rgba(91,122,102,0.3), rgba(91,122,102,0.08))",
              borderRadius: 1,
            }}
          />

          {reactions.map((r, i) => {
            const showDateHeader = i === 0 || r.date !== reactions[i - 1].date;
            return (
              <div key={i}>
                {showDateHeader && (
                  <div style={{ paddingLeft: 24, paddingBottom: 6, paddingTop: i === 0 ? 0 : 8 }}>
                    <BodyText
                      size={8}
                      color="var(--color-kin-muted)"
                      weight={700}
                      style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}
                    >
                      {r.date}
                    </BodyText>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 14,
                    paddingBottom: i === reactions.length - 1 ? 0 : 14,
                  }}
                >
                  {/* Concentric square node — matches SkillSigil glyph */}
                  <div style={{ marginTop: 1, flexShrink: 0, position: "relative", zIndex: 1 }}>
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        border: borderAlpha(sentimentColor[r.sentiment], "40", "1.5px"),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 1.5,
                          background: sentimentColor[r.sentiment],
                        }}
                      />
                    </div>
                  </div>

                  {/* Content — trigger datapoint + skill response */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* The datapoint */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <BodyText size={8.5} color="var(--color-kin-muted)" weight={700}>
                        {r.triggerLabel} · {r.time}
                      </BodyText>
                    </div>
                    <BodyText
                      size={9}
                      color="var(--color-kin-muted)"
                      weight={500}
                      style={{ lineHeight: 1.35, marginBottom: 4 }}
                    >
                      {r.trigger}
                    </BodyText>
                    {/* The reaction */}
                    <BodyText
                      size={10}
                      color="var(--color-kin-black)"
                      weight={600}
                      style={{ lineHeight: 1.45 }}
                    >
                      {r.message}
                    </BodyText>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Screen 5: Chat grounded in reactions ───────────────────────────────────

export function FlowReactionChat() {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div
        style={{
          padding: "8px 16px 6px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <DisplayText size={22} color="var(--color-kin-black)" lineHeight={1.1}>
          Kin
        </DisplayText>
        <div
          style={{
            padding: "4px 12px",
            borderRadius: 24,
            background: "rgba(91,122,102,0.15)",
            border: "1px solid rgba(91,122,102,0.3)",
            fontSize: 8.5,
            fontWeight: 800,
            color: "var(--color-kin-green-soft)",
            fontFamily: "'Manrope', system-ui",
            letterSpacing: "0.05em",
          }}
        >
          Nutrition Audit
        </div>
      </div>

      {/* Context strip — recent reaction summary */}
      <div
        className="phone-scroll"
        style={{
          display: "flex",
          gap: 6,
          padding: "0 16px 8px",
          flexShrink: 0,
          overflowX: "auto",
        }}
      >
        {[
          {
            label: "5-day streak",
            color: "var(--color-kin-green-soft)",
            bg: "rgba(91,122,102,0.15)",
          },
          {
            label: "Protein 28g avg",
            color: "var(--color-kin-green-soft)",
            bg: "rgba(91,122,102,0.15)",
          },
          {
            label: "3 reactions today",
            color: "var(--color-kin-gold)",
            bg: "rgba(255,208,40,0.15)",
          },
        ].map((ctx) => (
          <div
            key={ctx.label}
            style={{
              padding: "4px 10px",
              borderRadius: 20,
              background: ctx.bg,
              border: borderAlpha(ctx.color, "40"),
              fontSize: 8.5,
              fontWeight: 800,
              color: ctx.color,
              fontFamily: "'Manrope', system-ui",
              flexShrink: 0,
            }}
          >
            {ctx.label}
          </div>
        ))}
      </div>

      {/* Messages */}
      <div
        style={{
          flex: 1,
          padding: "0 14px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          overflowY: "hidden",
        }}
      >
        {/* Session divider */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 0",
          }}
        >
          <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.06)" }} />
          <BodyText size={8} color="var(--color-kin-muted)" weight={600} style={{ flexShrink: 0 }}>
            Today · after 3 reactions
          </BodyText>
          <div style={{ flex: 1, height: 1, background: "rgba(0,0,0,0.06)" }} />
        </div>

        {/* AI opening — grounded in reactions */}
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <div
            style={{
              maxWidth: "90%",
              background:
                "linear-gradient(135deg, rgba(91,122,102,0.12) 0%, rgba(91,122,102,0.04) 100%)",
              border: "1px solid rgba(91,122,102,0.25)",
              boxShadow: "0 4px 16px -4px rgba(91,122,102,0.1)",
              borderRadius: "6px 20px 20px 20px",
              padding: "12px 14px",
            }}
          >
            <BodyText
              size={11}
              color="var(--color-kin-black)"
              weight={500}
              style={{ lineHeight: 1.45 }}
            >
              You've hit 5 balanced dinners in a row — that's the longest streak since I started
              tracking. Your protein consistency is notably better than week 1.
            </BodyText>
          </div>
        </div>

        {/* User */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div
            style={{
              maxWidth: "75%",
              background: "rgba(254,253,251,0.9)",
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: "0 4px 12px -4px rgba(0,0,0,0.04)",
              borderRadius: "20px 20px 6px 20px",
              padding: "10px 14px",
            }}
          >
            <BodyText
              size={11}
              color="var(--color-kin-black)"
              weight={600}
              style={{ lineHeight: 1.4 }}
            >
              Am I getting enough omega-3?
            </BodyText>
          </div>
        </div>

        {/* AI — uses both reactions and vault data */}
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <div
            style={{
              maxWidth: "90%",
              background:
                "linear-gradient(135deg, rgba(91,122,102,0.12) 0%, rgba(91,122,102,0.04) 100%)",
              border: "1px solid rgba(91,122,102,0.25)",
              boxShadow: "0 4px 16px -4px rgba(91,122,102,0.1)",
              borderRadius: "6px 20px 20px 20px",
              padding: "12px 14px",
            }}
          >
            <BodyText
              size={11}
              color="var(--color-kin-black)"
              weight={500}
              style={{ lineHeight: 1.45 }}
            >
              You've had salmon twice this week and tuna once — that's solid. I can see from your
              supplement log you're also taking fish oil daily. Based on your labs, your omega-3
              index was 5.2% in February — aim for 8%+. The diet is helping but the supplement
              matters.
            </BodyText>
          </div>
        </div>

        {/* User follow-up */}
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <div
            style={{
              maxWidth: "75%",
              background: "rgba(254,253,251,0.9)",
              border: "1px solid rgba(255,255,255,0.6)",
              boxShadow: "0 4px 12px -4px rgba(0,0,0,0.04)",
              borderRadius: "20px 20px 6px 20px",
              padding: "10px 14px",
            }}
          >
            <BodyText
              size={11}
              color="var(--color-kin-black)"
              weight={600}
              style={{ lineHeight: 1.4 }}
            >
              What should I improve?
            </BodyText>
          </div>
        </div>

        {/* AI with specific, data-grounded suggestion */}
        <div style={{ display: "flex", justifyContent: "flex-start" }}>
          <div
            style={{
              maxWidth: "90%",
              background:
                "linear-gradient(135deg, rgba(91,122,102,0.12) 0%, rgba(91,122,102,0.04) 100%)",
              border: "1px solid rgba(91,122,102,0.25)",
              boxShadow: "0 4px 16px -4px rgba(91,122,102,0.1)",
              borderRadius: "6px 20px 20px 20px",
              padding: "12px 14px",
            }}
          >
            <BodyText
              size={11}
              color="var(--color-kin-black)"
              weight={500}
              style={{ lineHeight: 1.45 }}
            >
              Breakfast protein. I flagged this earlier today — your breakfasts average 12g vs 28g
              at lunch and 32g at dinner. Adding eggs or Greek yogurt would close the gap.
            </BodyText>
          </div>
        </div>
      </div>

      {/* Quick prompts */}
      <div style={{ position: "relative", flexShrink: 0, marginTop: 4 }}>
        <div
          className="phone-scroll"
          style={{
            padding: "6px 14px",
            display: "flex",
            gap: 8,
            overflowX: "auto",
            scrollbarWidth: "none",
          }}
        >
          {["Weekly summary", "Best meals this week", "Meal plan idea"].map((p) => (
            <PillChip key={p} color="var(--color-kin-green-soft)">
              {p}
            </PillChip>
          ))}
        </div>
        <div
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 32,
            background: "linear-gradient(to right, rgba(245,251,247,0), var(--color-kin-cream))",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Input */}
      <div style={{ padding: "6px 14px 0", flexShrink: 0 }}>
        <Glass
          style={{
            padding: "8px 10px 8px 16px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            borderRadius: 28,
            boxShadow: "0 4px 16px -4px rgba(0,0,0,0.06)",
          }}
        >
          <BodyText size={11} color="var(--color-kin-muted)" weight={500} style={{ flex: 1 }}>
            Ask about your nutrition…
          </BodyText>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              background: "var(--color-kin-green-soft)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 2px 8px rgba(91,122,102,0.4)",
            }}
          >
            <span
              style={{
                color: "var(--color-kin-white)",
                fontSize: 14,
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              ↑
            </span>
          </div>
        </Glass>
      </div>
    </div>
  );
}
